import type { Order } from '@/types/commerce'
import { EXTERNAL_PROVIDERS } from '@/constants'

export const ORDER_STATUSES = {
  /**
   * User paused the subscription. Fanatiz allows 2 billing cycles to reactivate.
   */
  PAUSED: 0,
  /**
   * User has an active order.
   */
  ACTIVE: 1,
  /**
   * User canceled – will transition to DEACTIVATED once the order expires.
   */
  CANCELED: 2,
  /**
   * Order has been deactivated.
   */
  DEACTIVATED: 4,
  /**
   * Payment was not completed.
   */
  PAST_DUE: 8,
  /**
   * User subscribed to a free trial plan.
   */
  TRIAL: 16,
  /**
   * User changed their subscription plan.
   */
  CHANGED: 32,
  /**
   * Payment is delayed or in process (pending state).
   */
  PENDING: 64,
} as const

/**
 * Statuses that still represent a live subscription relationship with the
 * product. `PAST_DUE` counts: the payment failed but the subscription is under
 * recovery, so the user is still a subscriber. `CANCELED` counts too: the
 * subscription runs until it expires and only then becomes `DEACTIVATED`.
 */
export const LIVE_SUBSCRIPTION_STATUSES: number[] = [
  ORDER_STATUSES.PAUSED,
  ORDER_STATUSES.ACTIVE,
  ORDER_STATUSES.TRIAL,
  ORDER_STATUSES.PENDING,
  ORDER_STATUSES.CANCELED,
  ORDER_STATUSES.PAST_DUE
]

/**
 * Backend commerce error codes (fx-npm-definitions `response.js`). Upstream
 * serializes them inconsistently (number or string) — compare via `String(code)`.
 */
export const COMMERCE_ERROR_CODES = {
  /** Purchase rejected because the account already has an order for this product (PRO-249). */
  ALREADY_ACTIVE_ORDER: '42217',
} as const

export const CHECKOUT_ORDER_FILTERS = {
  status: [
    ORDER_STATUSES.PAUSED,
    ORDER_STATUSES.ACTIVE,
    ORDER_STATUSES.CANCELED,
    ORDER_STATUSES.TRIAL,
  ],
}

/** A payment on the order failed and the subscription is awaiting recovery. */
export const isPastDue = (order?: Order): boolean => order?.status === ORDER_STATUSES.PAST_DUE

/**
 * Order paid through an external store/provider (App Store, Google Play, Roku,
 * telco deals). None of those gateway lambdas support manage events
 * (cancel/pause/resume/change/coupon), so the web must never offer them (PRO-240).
 */
export const isExternalProviderOrder = (order?: Order): boolean =>
  EXTERNAL_PROVIDERS.includes(order?.gateway?.type ?? '')
